import Footer from "../components/Footer";
import Newsletter from "../components/Newsletter";
import Category from "../components/Category";
import styled from "styled-components";
import { mobile, tablet } from "../responsive";
import { useHistory } from "react-router-dom";


const Hero = styled.div`
    position: relative;
    height: 80vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    background: linear-gradient(135deg, rgba(55, 72, 54, 0.9) 0%, rgba(68, 94, 79, 0.85) 50%, rgba(55, 72, 54, 0.95) 100%),
                url("https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80");
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    color: white;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(55, 72, 54, 0.3) 0%, rgba(68, 94, 79, 0.3) 50%, rgba(55, 72, 54, 0.4) 100%);
        z-index: 1;
    }

    & > * {
        position: relative;
        z-index: 2;
    }

    ${mobile({
        height: "60vh",
        backgroundAttachment: "scroll",
    })}

    ${tablet({
        height: "70vh",
        backgroundAttachment: "scroll",
    })}
`;

const HeroContent = styled.div`
    max-width: 800px;
    padding: 0 2rem;
    animation: fadeInUp 1s ease-out;
    
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const HeroTitle = styled.h1`
    font-size: 3.5rem;
    font-weight: 300;
    margin-bottom: 1.5rem;
    letter-spacing: -0.02em;
    line-height: 1.1;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

    ${mobile({
        fontSize: "2.5rem",
        marginBottom: "1rem",
    })}

    ${tablet({
        fontSize: "3rem",
        marginBottom: "1.25rem",
    })}
`;

const HeroSubtitle = styled.p`
    font-size: 1.25rem;
    font-weight: 300;
    margin-bottom: 2rem;
    opacity: 0.95;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;

    ${mobile({
        fontSize: "1.1rem",
        marginBottom: "1.5rem",
    })}
`;

const CTAButton = styled.button`
    background: rgba(68, 94, 79, 0.9);
    border: 2px solid rgba(255, 255, 255, 0.5);
    color: white;
    padding: 1rem 2.5rem;
    font-size: 1.1rem;
    font-weight: 400;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-family: "Jost-Bold";

    &:hover {
        background: rgba(55, 72, 54, 0.95);
        border-color: rgba(255, 255, 255, 0.8);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }

    ${mobile({
        padding: "0.875rem 2rem",
        fontSize: "1rem",
    })}
`;

// Features Section
const FeaturesSection = styled.section`
    padding: 80px 40px;
    background: var(--wellness-light);
    ${mobile({ padding: "60px 20px" })}
`;

const SectionTitle = styled.h2`
    font-size: 3rem;
    font-weight: 300;
    color: var(--wellness-primary);
    text-align: center;
    margin-bottom: 20px;
    font-family: "Jost-Bold";
    letter-spacing: -0.02em;

    ${mobile({ fontSize: "2rem" })}
`;

const SectionSubtitle = styled.p`
    font-size: 1.2rem;
    color: var(--wellness-secondary);
    text-align: center;
    max-width: 700px;
    margin: 0 auto 60px;
    line-height: 1.6;

    ${mobile({ fontSize: "1rem", marginBottom: "40px" })}
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    max-width: 1200px;
    margin: 0 auto;

    ${mobile({ gridTemplateColumns: "1fr", gap: "20px" })}
    ${tablet({ gridTemplateColumns: "repeat(2, 1fr)" })}
`;

const FeatureCard = styled.div`
    background: white;
    border-radius: 20px;
    padding: 40px 30px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(55, 72, 54, 0.08);
    transition: all 0.3s ease;
    border: 1px solid var(--wellness-neutral);

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 12px 40px rgba(55, 72, 54, 0.15);
        border-color: var(--wellness-secondary);
    }

    ${mobile({ padding: "30px 20px" })}
`;

const FeatureIcon = styled.div`
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, var(--wellness-primary) 0%, var(--wellness-secondary) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 25px;
    font-size: 2rem;
    color: white;
    font-weight: bold;
`;

const FeatureTitle = styled.h3`
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--wellness-primary);
    margin-bottom: 15px;
    font-family: "Jost-SemiBold";
`;

const FeatureDesc = styled.p`
    font-size: 1rem;
    color: var(--wellness-secondary);
    line-height: 1.6;
`;

// How It Works Section
const HowItWorksSection = styled.section`
    padding: 80px 40px;
    background: var(--wellness-warm);
    ${mobile({ padding: "60px 20px" })}
`;

const StepsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
    max-width: 1200px;
    margin: 40px auto 0;

    ${mobile({ gridTemplateColumns: "1fr", gap: "30px" })}
    ${tablet({ gridTemplateColumns: "repeat(3, 1fr)" })}
`;

const StepCard = styled.div`
    background: white;
    border-radius: 20px;
    padding: 40px 30px;
    text-align: center;
    position: relative;
    box-shadow: 0 4px 20px rgba(55, 72, 54, 0.08);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 40px rgba(55, 72, 54, 0.15);
    }

    ${mobile({ padding: "30px 20px" })}
`;

const StepNumber = styled.div`
    width: 60px;
    height: 60px;
    background: var(--wellness-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 1.8rem;
    color: white;
    font-weight: bold;
    font-family: "Jost-Bold";
`;

const StepTitle = styled.h3`
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--wellness-primary);
    margin-bottom: 15px;
    font-family: "Jost-SemiBold";
`;

const StepDesc = styled.p`
    font-size: 0.95rem;
    color: var(--wellness-secondary);
    line-height: 1.6;
`;

// Why Choose Us Section
const WhyChooseSection = styled.section`
    padding: 80px 40px;
    background: linear-gradient(135deg, var(--wellness-primary) 0%, var(--wellness-secondary) 100%);
    color: white;
    ${mobile({ padding: "60px 20px" })}
`;

const WhyChooseGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
    max-width: 1200px;
    margin: 40px auto 0;

    ${mobile({ gridTemplateColumns: "1fr", gap: "30px" })}
`;

const WhyCard = styled.div`
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 40px 30px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        transform: translateY(-5px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
    }

    ${mobile({ padding: "30px 20px" })}
`;

const WhyTitle = styled.h3`
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 20px;
    font-family: "Jost-Bold";

    ${mobile({ fontSize: "1.4rem" })}
`;

const WhyDesc = styled.p`
    font-size: 1.1rem;
    line-height: 1.8;
    opacity: 0.95;

    ${mobile({ fontSize: "1rem" })}
`;

const StatsSection = styled.section`
    padding: 80px 40px;
    background: var(--wellness-light);
    ${mobile({ padding: "60px 20px" })}
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 40px;
    max-width: 1200px;
    margin: 40px auto 0;

    ${mobile({ gridTemplateColumns: "repeat(2, 1fr)", gap: "30px" })}
`;

const StatCard = styled.div`
    text-align: center;
    padding: 30px 20px;
`;

const StatNumber = styled.div`
    font-size: 3.5rem;
    font-weight: bold;
    color: var(--wellness-primary);
    margin-bottom: 15px;
    font-family: "Jost-Bold";

    ${mobile({ fontSize: "2.5rem" })}
`;

const StatLabel = styled.div`
    font-size: 1.1rem;
    color: var(--wellness-secondary);
    font-family: "Jost-SemiBold";

    ${mobile({ fontSize: "0.95rem" })}
`;

// Product Showcase Section
const ProductShowcase = styled.section`
    padding: 80px 40px;
    background: var(--wellness-warm);
    ${mobile({ padding: "60px 20px" })}
`;

const ProductContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    align-items: center;

    ${mobile({ gridTemplateColumns: "1fr", gap: "40px" })}
`;

const ProductImage = styled.div`
    background: white;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(55, 72, 54, 0.15);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ProductContent = styled.div``;

const ProductTitle = styled.h2`
    font-size: 2.5rem;
    font-weight: 600;
    color: var(--wellness-primary);
    margin-bottom: 25px;
    font-family: "Jost-Bold";

    ${mobile({ fontSize: "2rem" })}
`;

const ProductDesc = styled.p`
    font-size: 1.1rem;
    color: var(--wellness-secondary);
    line-height: 1.8;
    margin-bottom: 30px;

    ${mobile({ fontSize: "1rem" })}
`;

const ProductFeatures = styled.ul`
    list-style: none;
    padding: 0;
    margin-bottom: 30px;

    li {
        padding: 15px 0;
        font-size: 1.05rem;
        color: var(--wellness-secondary);
        border-bottom: 1px solid var(--wellness-neutral);
        display: flex;
        align-items: center;

        &:before {
            content: "✓";
            color: var(--wellness-primary);
            font-size: 1.5rem;
            margin-right: 15px;
            font-weight: bold;
        }

        ${mobile({ fontSize: "0.95rem", padding: "12px 0" })}
    }
`;

const ProductButton = styled.button`
    background: var(--wellness-primary);
    color: white;
    border: none;
    padding: 15px 40px;
    font-size: 1.1rem;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: "Jost-Bold";
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
        background: var(--wellness-secondary);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(55, 72, 54, 0.3);
    }

    ${mobile({ padding: "12px 30px", fontSize: "1rem" })}
`;

const Home = () => {
    const history = useHistory();

    const features = [
        { icon: "🌿", title: "Eliminates Radiation", desc: "Protects you from harmful radiation in your environment." },
        { icon: "🧪", title: "Body Detoxification", desc: "Assists in discharging accumulated harmful radiation." },
        { icon: "🏠", title: "Clean Environment", desc: "Removes radiation and pollutants from your home." },
        { icon: "⚡", title: "Cellular Enhancement", desc: "Maximizes the efficiency of your body's cells." },
        { icon: "🦠", title: "Microorganism Control", desc: "Helps manage bacteria, viruses, and mites." },
        { icon: "💚", title: "Immune Support", desc: "Allows your immune system to rest and recharge." }
    ];

    return (
        <div>
            <Hero>
                <HeroContent>
                    <HeroTitle>Breathe Pure, Live Well</HeroTitle>
                    <HeroSubtitle>
                        Discover the power of clean air and wellness. Transform your space into a sanctuary of health and tranquility.
                    </HeroSubtitle>
                    <CTAButton onClick={() => history.push("/product/G10")}>Explore Products</CTAButton>
                </HeroContent>
            </Hero>

            {/* Features Section */}
            <FeaturesSection>
                <SectionTitle>Why Choose Air Vitamin G10?</SectionTitle>
                <SectionSubtitle>
                    Experience cutting-edge technology designed to purify your environment and enhance your wellbeing
                </SectionSubtitle>
                <FeaturesGrid>
                    {features.map((feature, index) => (
                        <FeatureCard key={index}>
                            <FeatureIcon>{feature.icon}</FeatureIcon>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDesc>{feature.desc}</FeatureDesc>
                        </FeatureCard>
                    ))}
                </FeaturesGrid>
            </FeaturesSection>

            {/* Product Showcase */}
            <ProductShowcase>
                <SectionTitle>Meet Air Vitamin G10</SectionTitle>
                <SectionSubtitle>The Essential Health Partner for Modern Living</SectionSubtitle>
                <ProductContainer>
                    <ProductImage>
                        <img src="https://ik.imagekit.io/dnddecpho/airvitamin/Air%20Vitamin_-dMqEE_hn.jpg?updatedAt=1728393364012" alt="Air Vitamin G10" />
                    </ProductImage>
                    <ProductContent>
                        <ProductTitle>Your Essential Health Partner</ProductTitle>
                        <ProductDesc>
                            Air Vitamin G10 is a patented invention, trademarked since 2004. With over 20 years of customer satisfaction, 
                            this revolutionary air purifier transforms your living space into a sanctuary of health.
                        </ProductDesc>
                        <ProductFeatures>
                            <li>10-Year After-Sales Service Guarantee</li>
                            <li>1-Year Product Warranty</li>
                            <li>Energy Efficient (10W, 12V)</li>
                            <li>Safe & Cost-Effective Operation</li>
                            <li>Reduces Air Pollution Effectively</li>
                            <li>Long-Term Health Support</li>
                        </ProductFeatures>
                        <ProductButton onClick={() => history.push("/product/G10")}>View Product Details</ProductButton>
                    </ProductContent>
                </ProductContainer>
            </ProductShowcase>

            {/* How It Works */}
            <HowItWorksSection>
                <SectionTitle style={{ color: "var(--wellness-primary)" }}>How It Works</SectionTitle>
                <SectionSubtitle style={{ color: "var(--wellness-secondary)" }}>
                    Simple, effective, and designed for your wellbeing
                </SectionSubtitle>
                <StepsContainer>
                    <StepCard>
                        <StepNumber>1</StepNumber>
                        <StepTitle>Place & Power</StepTitle>
                        <StepDesc>
                            Simply place your Air Vitamin G10 in your living space and power it on. The device operates on just 10W of power.
                        </StepDesc>
                    </StepCard>
                    <StepCard>
                        <StepNumber>2</StepNumber>
                        <StepTitle>Continuous Purification</StepTitle>
                        <StepDesc>
                            Our innovative technology works 24/7 to eliminate harmful radiation, purify air, and control microorganisms.
                        </StepDesc>
                    </StepCard>
                    <StepCard>
                        <StepNumber>3</StepNumber>
                        <StepTitle>Enjoy Wellness</StepTitle>
                        <StepDesc>
                            Experience improved air quality, enhanced cellular function, and a stronger immune system. Your health partner never stops working.
                        </StepDesc>
                    </StepCard>
                </StepsContainer>
            </HowItWorksSection>

            {/* Stats Section */}
            <StatsSection>
                <SectionTitle>Our Impact</SectionTitle>
                <SectionSubtitle>
                    Trusted by thousands for over two decades
                </SectionSubtitle>
                <StatsGrid>
                    <StatCard>
                        <StatNumber>20+</StatNumber>
                        <StatLabel>Years Experience</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatNumber>10K+</StatNumber>
                        <StatLabel>Satisfied Customers</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatNumber>99%</StatNumber>
                        <StatLabel>Satisfaction Rate</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatNumber>10Y</StatNumber>
                        <StatLabel>Service Guarantee</StatLabel>
                    </StatCard>
                </StatsGrid>
            </StatsSection>

            {/* Why Choose Us */}
            <WhyChooseSection>
                <SectionTitle style={{ color: "white" }}>Why Choose Air Vitamin?</SectionTitle>
                <SectionSubtitle style={{ color: "rgba(255, 255, 255, 0.95)" }}>
                    Excellence in every aspect of your health journey
                </SectionSubtitle>
                <WhyChooseGrid>
                    <WhyCard>
                        <WhyTitle>💎 Patented Innovation</WhyTitle>
                        <WhyDesc>
                            Our revolutionary technology has been trademarked since 2004, with over 20 years of proven results in Malaysia and overseas.
                        </WhyDesc>
                    </WhyCard>
                    <WhyCard>
                        <WhyTitle>🔧 Lifetime Support</WhyTitle>
                        <WhyDesc>
                            We service and repair Air Vitamin products that are over 10 years old. Great products deserve great service and parts availability.
                        </WhyDesc>
                    </WhyCard>
                    <WhyCard>
                        <WhyTitle>⚡ Energy Efficient</WhyTitle>
                        <WhyDesc>
                            Operating on just 10W of power, Air Vitamin G10 is both safe and cost-effective while delivering maximum health benefits.
                        </WhyDesc>
                    </WhyCard>
                    <WhyCard>
                        <WhyTitle>🏆 Proven Results</WhyTitle>
                        <WhyDesc>
                            Thousands of satisfied customers trust Air Vitamin for radiation elimination, air purification, and immune system support.
                        </WhyDesc>
                    </WhyCard>
                </WhyChooseGrid>
            </WhyChooseSection>

            <Category />
            <Newsletter />
            <Footer />
        </div>
    );
};

export default Home;
